/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, Camera, X, Loader2, Check, Plus, MapPin, Cloud, Clock,
  Package, MessageSquare, Hash
} from 'lucide-react';
import { Button } from './ui/button';
import CatchImage from './CatchImage';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FISH_TYPES = [
  'Common Carp', 'Mirror Carp', 'Linear Carp', 'Ghost Carp', 'Leather Carp', 'Grass Carp',
  'Tench', 'Bream', 'Pike', 'Other',
];

const SESSION_DURATIONS = [
  '< 4 hours', '4-8 hours', 'Day session', 'Overnight', '24 hours', '48 hours', '72+ hours',
];

const WEATHER_OPTIONS = [
  'Sunny', 'Overcast', 'Rainy', 'Windy', 'Misty', 'Cold', 'Mild', 'Warm',
];

const initialForm = {
  fish_type: 'Common Carp',
  weight: '',
  length: '',
  lake_name: '',
  bait_used: '',
  session_duration: '',
  weather: '',
  swim_number: '',
  bundle_used: '',
  bundle_name: '',
  notes: '',
  image_url: '',
  caught_at: '',
};

export default function SubmitCatchModal({ open, onClose, leagueId, token, bundles, onSuccess }) {
  const { refreshUser } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [usedBundle, setUsedBundle] = useState('no');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  const reset = () => {
    setForm(initialForm);
    setUsedBundle('no');
    setBreakdown(null);
  };

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 10MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    // Client-side resize + JPEG re-encode, then store the result as a base64
    // data URI directly on `image_url`. Identical strategy to Community's
    // share-catch flow (Community.jsx::handleImageSelect). Why:
    //   1. The previous /api/upload/image path wrote to the pod's local disk,
    //      which is wiped on every redeploy — so any League catch uploaded
    //      that way silently broke as soon as the next deploy happened. That
    //      is exactly why the user saw their photo in Community/Profile (the
    //      mirror row showed the same /api/uploads/.. URL but Community kept
    //      working because most fresh catches had been base64'd via the
    //      community flow) yet missing in the League Recent Catches feed —
    //      and earlier League submissions stored as paths now 404.
    //   2. A data URI survives every redeploy, every pod restart, every
    //      ephemeral-disk wipe. Mongo holds the bytes, full stop.
    //   3. The submit POST already accepts `image_url` so no backend change.
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = document.createElement('img');
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const dataUri = canvas.toDataURL('image/jpeg', 0.82);
        update('image_url', dataUri);
        setUploadingImage(false);
        toast.success('Photo ready');
      };
      img.onerror = () => {
        toast.error('Could not read that image. Try a different photo.');
        setUploadingImage(false);
      };
      img.src = ev.target.result;
    };
    reader.onerror = () => {
      toast.error('Could not read that image.');
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.weight || !form.lake_name) {
      toast.error('Weight and lake name are required');
      return;
    }
    if (!form.image_url) {
      toast.error('Photo is required for every catch');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        league_id: leagueId,
        weight: parseFloat(form.weight),
        length: form.length ? parseFloat(form.length) : null,
        bundle_used: usedBundle === 'yes' ? form.bundle_used : null,
        bundle_name: usedBundle === 'yes' ? form.bundle_name : null,
      };
      const r = await axios.post(`${API}/leagues/${leagueId}/catches`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreakdown({ total: r.data.points_earned, items: r.data.breakdown });
      toast.success(`+${r.data.points_earned} points!`);
      if (r.data.trial_granted) {
        toast.success(
          `Pro trial unlocked for ${r.data.trial_granted.trial_days} days — full access on us!`,
          { duration: 8000 }
        );
        // Refresh user so the trial banner appears on Dashboard.
        refreshUser?.();
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  if (breakdown) {
    return (
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md" data-testid="points-breakdown-modal">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
              <Check className="w-6 h-6 text-[#22c55e]" />
              {breakdown.total} Points Earned
            </DialogTitle>
            <DialogDescription className="text-[#8a948a]">
              Here's how your points were calculated.
            </DialogDescription>
          </DialogHeader>
          <ul className="mt-4 space-y-2">
            {breakdown.items.map((b, i) => (
              <li
                key={i}
                className="flex justify-between items-center p-2.5 bg-[#141714] rounded-lg"
                data-testid={`breakdown-${b.key}`}
              >
                <span className="text-[#e2e4e2] text-sm">{b.label}</span>
                <span className="font-['Barlow_Condensed'] text-base font-bold text-[#c8b273]">+{b.points}</span>
              </li>
            ))}
            <li className="flex justify-between items-center p-3 mt-2 bg-[#c8b273]/15 border border-[#c8b273]/40 rounded-lg">
              <span className="font-['Barlow_Condensed'] uppercase tracking-wider text-[#c8b273]">Total</span>
              <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{breakdown.total}</span>
            </li>
          </ul>
          <Button className="btn-gold w-full mt-4" onClick={close} data-testid="breakdown-close-btn">
            Done
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="submit-catch-modal">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-wider text-white flex items-center gap-2">
            <Upload className="w-6 h-6 text-[#c8b273]" />
            Log Your Catch
          </DialogTitle>
          <DialogDescription className="text-[#8a948a]">
            All required fields earn points. Optional fields are bonus.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Photo - REQUIRED */}
          <div>
            <label className="text-sm text-[#8a948a] block mb-1">
              Photo <span className="text-red-400">*</span> <span className="text-[#22c55e] text-xs">(+5 pts, mandatory)</span>
            </label>
            {form.image_url ? (
              <div className="relative">
                <CatchImage
                  row={form}
                  aspectRatio="auto"
                  className="w-full h-44 rounded-lg"
                  alt="Catch preview"
                />
                <Button type="button" variant="ghost" size="sm"
                  className="absolute top-2 right-2 bg-black/60"
                  onClick={() => update('image_url', '')}
                  data-testid="catch-photo-remove"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#c8b273]/40 rounded-lg cursor-pointer hover:border-[#c8b273] transition-colors bg-[#141714]" data-testid="catch-photo-upload-label">
                {uploadingImage ? (
                  <Loader2 className="w-8 h-8 animate-spin text-[#c8b273]" />
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-[#c8b273] mb-2" />
                    <span className="text-[#8a948a] text-sm">Tap to upload photo (required)</span>
                  </>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} data-testid="catch-photo-input" />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Species *</label>
              <select
                className="w-full bg-[#141714] border border-white/10 rounded-lg px-3 py-2 text-white"
                value={form.fish_type}
                onChange={(e) => update('fish_type', e.target.value)}
                data-testid="catch-fish-type"
              >
                {FISH_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Weight (lbs) *</label>
              <Input type="number" step="0.1" placeholder="e.g. 24.5"
                value={form.weight} onChange={(e) => update('weight', e.target.value)}
                className="bg-[#141714] border-white/10" required data-testid="catch-weight"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-[#8a948a] block mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Lake Name *</label>
            <Input placeholder="e.g. Linear Fisheries"
              value={form.lake_name} onChange={(e) => update('lake_name', e.target.value)}
              className="bg-[#141714] border-white/10" required data-testid="catch-lake"
            />
            <p className="text-[10px] text-[#6b7d6b] mt-1">First time logging a lake = +15 pts (new lake bonus)</p>
          </div>

          {/* Session Block */}
          <div className="p-3 rounded-lg bg-[#141714]/60 border border-white/5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold">Session details <span className="text-[#22c55e]">(+5 if all 3 filled)</span></p>
            <div>
              <label className="text-sm text-[#8a948a] block mb-1">Bait used</label>
              <Input placeholder="e.g. Sticky Krill 15mm boilies"
                value={form.bait_used} onChange={(e) => update('bait_used', e.target.value)}
                className="bg-[#141714] border-white/10" data-testid="catch-bait"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#8a948a] block mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Session duration</label>
                <select
                  className="w-full bg-[#141714] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  value={form.session_duration} onChange={(e) => update('session_duration', e.target.value)}
                  data-testid="catch-duration"
                >
                  <option value="">Select…</option>
                  {SESSION_DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#8a948a] block mb-1 flex items-center gap-1"><Cloud className="w-3.5 h-3.5" /> Weather</label>
                <select
                  className="w-full bg-[#141714] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  value={form.weather} onChange={(e) => update('weather', e.target.value)}
                  data-testid="catch-weather"
                >
                  <option value="">Select…</option>
                  {WEATHER_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#8a948a] block mb-1 flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Swim/peg number (optional)</label>
              <Input placeholder="e.g. Peg 12"
                value={form.swim_number} onChange={(e) => update('swim_number', e.target.value)}
                className="bg-[#141714] border-white/10" data-testid="catch-swim"
              />
            </div>
          </div>

          {/* Bundle Block */}
          <div className="p-3 rounded-lg bg-[#141714]/60 border border-white/5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Did you use a Carp Cast bundle? <span className="text-[#22c55e]">(+10)</span></p>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 text-sm text-[#e2e4e2] cursor-pointer">
                <input type="radio" name="bundle" value="no" checked={usedBundle === 'no'} onChange={() => setUsedBundle('no')} data-testid="bundle-no" />
                No
              </label>
              <label className="flex items-center gap-2 text-sm text-[#e2e4e2] cursor-pointer">
                <input type="radio" name="bundle" value="yes" checked={usedBundle === 'yes'} onChange={() => setUsedBundle('yes')} data-testid="bundle-yes" />
                Yes
              </label>
            </div>
            {usedBundle === 'yes' && (
              <select
                className="w-full bg-[#141714] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                value={form.bundle_used}
                onChange={(e) => {
                  const key = e.target.value;
                  const b = bundles?.[key];
                  update('bundle_used', key);
                  update('bundle_name', b?.name || '');
                }}
                data-testid="catch-bundle-select"
              >
                <option value="">Pick a bundle…</option>
                {bundles && Object.entries(bundles).map(([k, b]) => (
                  <option key={k} value={k}>{b.name}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="text-sm text-[#8a948a] block mb-1 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Notes (optional)</label>
            <Textarea placeholder="Tell us about the catch…"
              value={form.notes} onChange={(e) => update('notes', e.target.value)}
              className="bg-[#141714] border-white/10 h-16" data-testid="catch-notes"
            />
          </div>

          <div className="text-[10px] text-[#6b7d6b] flex flex-wrap gap-x-3 gap-y-0.5">
            <span>Night catch (8pm–6am) +5</span>
            <span>•</span>
            <span>Back-to-back &lt;12h +10</span>
            <span>•</span>
            <span>First fish of week +10</span>
          </div>

          <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="catch-submit-btn">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />Submit Catch</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
